import React, { useEffect, useState } from "react";
import { Dialog, Input, toastMessage } from "../utils";
import { useDeleteUnassignedDockBayVisitMutation, useAssignDockBayVehicleMutation, useGetUnassignedDockBayVisitsQuery } from "../../api/vmsSlice";
import FilePreview from "../utils/FilePreview";
import { useDockChangeRefetch, DOCK_FALLBACK_POLL_MS } from "./useDockRefresh";

function VisitPhoto({ visit }) {
    const path = visit?.cameraEvidence?.imagePath;
    return <div className="dock-identification-photo">
        {path ? <>
            <FilePreview key={path} fileKey={path} extension={String(path).split(".").pop()} fallbackText="Camera photo unavailable" />
            <small>Camera photo from this visit{visit.cameraEvidence.reportedVehicleNumber ? ` · Read as ${visit.cameraEvidence.reportedVehicleNumber}` : ""}. Click photo to enlarge.</small>
        </> : <small>No camera photo available for this visit.</small>}
    </div>;
}

export default function UnassignedVisits({ plantId, enabled, checkpoints = [], canDelete = false, onAssigned, selectedVisitId, clearSelectedVisit }) {
    const checkpointName = visit => checkpoints.find(({ checkpoint }) => String(checkpoint.checkpointNumber) === String(visit.checkpointNumber))?.checkpoint.displayName || `Bay ${visit.checkpointNumber}`;
    const [page, setPage] = useState(1);
    const query = useGetUnassignedDockBayVisitsQuery({ plantId, page_no: selectedVisitId ? 1 : page, ...(selectedVisitId ? { bayVisitId: selectedVisitId } : {}) }, { skip: !plantId || !enabled, pollingInterval: DOCK_FALLBACK_POLL_MS });
    useDockChangeRefetch(plantId, query.refetch);
    const [assign, result] = useAssignDockBayVehicleMutation();
    const [removeVisit, deletion] = useDeleteUnassignedDockBayVisitMutation();
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteError, setDeleteError] = useState("");
    const confirmDelete = async () => {
        try {
            const response = await removeVisit({ plantId, bayVisitId: deleteTarget._id }).unwrap();
            if (response?.error) return setDeleteError(response.message || "Deletion failed. Please retry.");
            setDeleteTarget(null); setDeleteError("");
            if (page > 1 && (query.data?.data || []).length === 1) setPage(page - 1);
            query.refetch(); onAssigned();
            toastMessage(true, "Verification visit deleted.");
        } catch (failure) { setDeleteError(failure?.data?.message || "Deletion failed. Please retry."); }
    };
    const closeDelete = () => { if (!deletion.isLoading) { setDeleteTarget(null); setDeleteError(""); } };
    const [target, setTarget] = useState(null);
    const [number, setNumber] = useState("");
    const [error, setError] = useState("");
    const close = () => { if (!result.isLoading) { setTarget(null); setError(""); } };
    const submit = async () => {
        if (!number.trim()) return setError("Enter the observed vehicle number.");
        try {
            const response = await assign({ plantId, bayVisitId: target._id, vehicleNumber: number.trim() }).unwrap();
            if (response?.error) return setError(response.message || "Assignment failed.");
            setTarget(null); setError(""); query.refetch(); onAssigned();
            toastMessage(true, "Vehicle assigned. Original Bay events retained.");
        } catch (failure) { setError(failure?.data?.message || "Assignment failed. Please retry."); }
    };
    const visits = query.data?.data || [];
    useEffect(() => {
        if (!selectedVisitId) return;
        const selected = visits.find(visit => visit._id === selectedVisitId);
        if (selected) { setTarget(selected); setNumber(""); setError(""); clearSelectedVisit(); }
    }, [selectedVisitId, query.data, clearSelectedVisit]);
    if (!visits.length && !target && !deleteTarget && page === 1 && !query.error && !query.data?.error) return null;
    return <section className="dock-queue-panel dock-unassigned-panel" aria-label="Vehicles requiring identification">
        <div className="dock-queue-panel-header dock-identification-header">
            <h3>Vehicles requiring identification</h3>
            <span className="dock-identification-count">{query.data?.count || visits.length} vehicles</span>
        </div>
        {(query.error || query.data?.error) && <p role="alert">Unable to load unassigned visits. Please refresh.</p>}
        <p className="dock-identification-description">Confirm the observed vehicle number to identify each visit. New vehicles are added automatically when assigned.</p>
        <div className="dock-identification-list">
        {visits.map(visit => <div className="dock-identification-row" key={visit._id}>
            <div className="dock-identification-details">
                <span className="dock-identification-bay">{checkpointName(visit)}</span>
                <strong>{visit.displayVehicleNumber || "Unidentified vehicle"}</strong>
                <span className={`dock-status ${visit.open ? "dock-status-occupied" : "dock-status-unknown"}`}>{visit.open ? "Occupied" : "Departed"}</span>
            </div>
            <VisitPhoto visit={visit} />
            <button type="button" className="dock-card-action-btn dock-card-action-assign" onClick={() => { setTarget(visit); setNumber(""); setError(""); }}>Assign vehicle</button>
            {canDelete && <button type="button" className="dock-card-action-btn" onClick={() => { setDeleteTarget(visit); setDeleteError(""); }}>Delete visit</button>}
        </div>)}
        </div>
        <nav className="dock-identification-pagination" aria-label="Vehicle identification pages">
            <span>Page {page}</span>
            <div>
                <button type="button" className="dock-card-action-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
                <button type="button" className="dock-card-action-btn" disabled={page * (query.data?.pagesize || 50) >= (query.data?.count || 0)} onClick={() => setPage(page + 1)}>Next</button>
            </div>
        </nav>
        {deleteTarget && <Dialog size="md" open title="Delete verification visit" handleClose={closeDelete} handleCancel={closeDelete}
            handleSubmit={confirmDelete} SubmitText="Delete visit" isSubmitDisabled={deletion.isLoading}>
            <p>Delete the capture for {deleteTarget.displayVehicleNumber}? It will be removed from the identification list and any Bay occupancy belonging to this visit will be released.</p>
            <VisitPhoto visit={deleteTarget} />
            {deleteError && <p role="alert">{deleteError}</p>}
        </Dialog>}
        {target && <Dialog size="md" open title={`Identify vehicle — ${checkpointName(target)}`} handleClose={close} handleCancel={close}
            handleSubmit={submit} SubmitText="Assign vehicle" isSubmitDisabled={result.isLoading}>
            <VisitPhoto visit={target} />
            <Input id="dock-assign-vehicle" name="vehicleNumber" label="Observed vehicle number" value={number}
                onChange={event => setNumber(event.target.value)} required />
            {error && <p role="alert">{error}</p>}
        </Dialog>}
    </section>;
}
